/**
 * A document body stored as one record per block (25.10.2026), so a save
 * sends — and a teammate receives — only the paragraphs that changed.
 *
 * Before, the whole body was ONE `docbody` record: every autosave pushed the
 * whole document, the server kept whichever whole copy landed last, and its
 * live broadcast sent the whole document to every member of the team on
 * every save (sync.ex format_entity sends a row's full data).
 *
 *   docblock record  id `<docId>::b:<blockId>`, parent_id = docId,
 *                    sort_key = fractional key (order, fractionalKeys.js),
 *                    data = { scope_id: docId, bid: blockId, node }
 *
 * `node` is the block's Lexical node in the docbody codec's compact form.
 * `bid` is the editor's block id (the record id may be re-keyed by a device
 * migration, which rewrites parent_id and scope_id but not data, so the block
 * id is read from `bid`, never parsed out of the record id).
 *
 * Different people's paragraphs are different records: they never overwrite
 * each other on the server. One paragraph edited by two people at once is
 * merged live (lexicalMerge.js); the record keeps the last save of it.
 */
import { compactBlockNode, expandBlockNode } from './docBodyCodec';
import { reassignKeys } from './fractionalKeys';
import { seededBlockIds, stableStringify } from '../../../components/ui/lexicalBlocks';

export const BLOCK_RECORD_TYPE = 'docblock';

export const blockRecordId = (docId, blockId) => `${docId}::b:${blockId}`;

/**
 * The key a block from the document's original HTML gets on its own: from its
 * place in that HTML (its id is seeded from the document id and that index —
 * lexicalBlocks.seededBlockIds, the ids every editor derives for it). Two
 * people moving the same page to blocks at once, each before the other's
 * records reached them, write the same keys, so the order agrees.
 */
export function seedKey(docId, blockId) {
  const prefix = seededBlockIds(docId, 1)[0].replace(/_0$/, '_');
  if (typeof blockId !== 'string' || !blockId.startsWith(prefix)) return null;
  const index = parseInt(blockId.slice(prefix.length), 36);
  if (!Number.isInteger(index) || index < 0 || String(index.toString(36)) !== blockId.slice(prefix.length)) return null;
  // '1' + six base-62 digits: ordered by index, with room between neighbours.
  const digits = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
  let n = (index + 1) * 64;
  let out = '';
  for (let i = 0; i < 6; i += 1) {
    out = digits[n % 62] + out;
    n = Math.floor(n / 62);
  }
  return `1${out}`;
}

export const isBlockRow = (row) => !!row && row.record_type === BLOCK_RECORD_TYPE && !row.deleted && typeof row.bid === 'string';

const byOrder = (a, b) => {
  const ka = a.sort_key || '';
  const kb = b.sort_key || '';
  if (ka !== kb) return ka < kb ? -1 : 1;
  return a.bid < b.bid ? -1 : a.bid > b.bid ? 1 : 0;
};

/**
 * Block rows of one document → its editor snapshot plus what a later save
 * needs to know: each block's key, record id and stored node (as JSON).
 * Ordered by key, then block id — the same on every device. A block id seen
 * twice (cannot normally happen) keeps its first row.
 */
export function blocksFromRows(rows) {
  const live = (rows || []).filter(isBlockRow).sort(byOrder);
  const blocks = [];
  const keys = new Map();
  const recordIds = new Map();
  const stored = new Map();
  const nodes = new Map();
  for (const row of live) {
    if (keys.has(row.bid)) continue;
    const node = expandBlockNode(row.node);
    blocks.push({ id: row.bid, node });
    keys.set(row.bid, row.sort_key || null);
    recordIds.set(row.bid, row.id);
    stored.set(row.bid, stableStringify(node));
    nodes.set(row.bid, node);
  }
  return { snapshot: { version: 1, blocks }, keys, recordIds, stored, nodes };
}

/**
 * The writes that turn the stored blocks (`current`, from blocksFromRows)
 * into `snapshot`: an upsert for every block whose content or position
 * changed, a delete for every block that is gone. Unchanged blocks are not
 * written at all — that is the whole point.
 *
 * `onlyIds` (a Set of block ids, or null for all): write only these — the
 * blocks the person at this screen changed (useDocumentBody.js). A teammate's
 * paragraph shown here live is newer on screen than in this device's store
 * until their save arrives; writing it from here would send everyone's typing
 * from every screen and could land an older copy last.
 */
export function planBlockWrites(docId, current, snapshot, { onlyIds = null, deletedHere = null, movedIds = null } = {}) {
  const blocks = (snapshot && Array.isArray(snapshot.blocks) ? snapshot.blocks : []).filter((b) => b && typeof b.id === 'string');
  const seen = new Set();
  const ordered = [];
  for (const b of blocks) {
    if (seen.has(b.id)) continue;
    seen.add(b.id);
    ordered.push(b);
  }
  const keyOf = (id) => current.keys.get(id) ?? seedKey(docId, id);
  const keyChanges = reassignKeys(ordered.map((b) => ({ id: b.id, key: keyOf(b.id) })));
  const upserts = [];
  for (const b of ordered) {
    const mine = !onlyIds || onlyIds.has(b.id);
    const newKey = keyChanges.get(b.id);
    const contentChanged = current.stored.get(b.id) !== stableStringify(b.node);
    // A block moved HERE (dragged — its content did not change, so it is not
    // among the changed ids; `movedIds` from blockBaseline.movedSince) keeps
    // its STORED content and gets its new key. A block merely out of order on
    // this screen (a teammate's drag not shown here yet) is left alone:
    // writing its old place back would undo their move. A full save (no
    // `onlyIds`) writes every key change.
    const moved = newKey !== undefined && current.keys.has(b.id) && (!onlyIds || (movedIds && movedIds.has(b.id)));
    if (!(mine && (contentChanged || newKey !== undefined)) && !moved) continue;
    const node = mine ? b.node : current.nodes.get(b.id);
    upserts.push({
      id: current.recordIds.get(b.id) || blockRecordId(docId, b.id),
      record_type: BLOCK_RECORD_TYPE,
      parent_id: docId,
      sort_key: newKey ?? keyOf(b.id),
      data: { scope_id: docId, bid: b.id, node: compactBlockNode(node) },
    });
  }
  const deletes = [];
  for (const [bid, recordId] of current.recordIds) {
    if (!seen.has(bid) && (!onlyIds || onlyIds.has(bid))) deletes.push(recordId);
  }
  // Deleted here, but its record has not reached this device yet (a
  // teammate's line seen live): the store cannot delete what it does not
  // hold, so it is written first and deleted right after — on the server the
  // delete lands last.
  for (const bid of deletedHere || []) {
    if (seen.has(bid) || current.recordIds.has(bid)) continue;
    const id = blockRecordId(docId, bid);
    // keyed after everything: should the delete never land, it is an empty
    // line at the end, not one in the middle of the page
    upserts.push({ id, record_type: BLOCK_RECORD_TYPE, parent_id: docId, sort_key: 'zzzzzzzz', data: { scope_id: docId, bid, node: { children: [] } } });
    deletes.push(id);
  }
  return { upserts, deletes };
}

/**
 * The largest block record `snapshot` would store, in bytes of its JSON —
 * one block, not the document, is what must fit in a synced record now.
 */
export function largestBlockBytes(docId, snapshot) {
  let max = 0;
  for (const b of (snapshot && snapshot.blocks) || []) {
    const bytes = JSON.stringify({ scope_id: docId, bid: b.id, node: compactBlockNode(b.node) }).length;
    if (bytes > max) max = bytes;
  }
  return max;
}

// Editor state an HTML round trip does not keep: a paragraph's remembered
// caret format and style, its text direction.
const CARET_STATE = new Set(['textFormat', 'textStyle', 'direction']);

const plainText = (x) => {
  if (!x || typeof x !== 'object') return '';
  if (typeof x.text === 'string') return x.text;
  if (x.type === 'linebreak') return '\n';
  return (x.children || []).map(plainText).join('');
};

/**
 * What "the same block" means across an HTML round trip: the node without
 * caret state; a code block by its language and text (its highlight tokens
 * are cut again on import).
 */
function matchKey(node) {
  const strip = (n) => {
    if (!n || typeof n !== 'object') return n;
    if (n.type === 'code') return { type: 'code', language: n.language || null, text: plainText(n) };
    const out = {};
    for (const [k, v] of Object.entries(n)) {
      if (CARET_STATE.has(k)) continue;
      out[k] = k === 'children' && Array.isArray(v) ? v.map(strip) : v;
    }
    return out;
  };
  return stableStringify(strip(node));
}

/**
 * A snapshot made from HTML (an AI / MCP / git write) takes the ids of the
 * stored blocks it still contains — same content, preferably at the same
 * place — so rewriting a page changes the blocks that changed instead of
 * deleting and re-creating every one (and a teammate's caret in an unchanged
 * paragraph stays in it). Only blocks with no stored twin keep the derived id.
 */
export function adoptStoredIds(snapshot, current) {
  if (!snapshot || !Array.isArray(snapshot.blocks) || !current.recordIds.size) return snapshot;
  const byContent = new Map();
  current.snapshot.blocks.forEach((b, i) => {
    const key = matchKey(b.node);
    if (!byContent.has(key)) byContent.set(key, []);
    byContent.get(key).push({ id: b.id, index: i });
  });
  const taken = new Set();
  const blocks = snapshot.blocks.map((b, i) => {
    const twins = (byContent.get(matchKey(b.node)) || []).filter((t) => !taken.has(t.id));
    if (!twins.length) return { block: b, adopted: false };
    const best = twins.reduce((x, y) => (Math.abs(y.index - i) < Math.abs(x.index - i) ? y : x));
    taken.add(best.id);
    return { block: { ...b, id: best.id }, adopted: true };
  });
  // A derived id that collides with an adopted one (or another derived one)
  // is renamed apart — the adopted id keeps its stored record.
  const used = new Set(blocks.map((x) => x.block.id));
  const claimed = new Set(blocks.filter((x) => x.adopted).map((x) => x.block.id));
  return {
    ...snapshot,
    blocks: blocks.map(({ block, adopted }) => {
      if (adopted || !claimed.has(block.id)) {
        claimed.add(block.id);
        return block;
      }
      let n = 1;
      while (used.has(`${block.id}_${n}`)) n += 1;
      const id = `${block.id}_${n}`;
      used.add(id);
      claimed.add(id);
      return { ...block, id };
    }),
  };
}
